import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

const Test2 = () => {
    const [showWinModal, setShowWinModal] = useState(false);
    const [showLossModal, setShowLossModal] = useState(false);
    const timerRef = useRef(null);

    // Auto close after 3 seconds
    useEffect(() => {
        if (showWinModal) {
            timerRef.current = setTimeout(() => {
                setShowWinModal(false);
            }, 3000);
        }

        if (showLossModal) {
            timerRef.current = setTimeout(() => {
                setShowLossModal(false);
            }, 3000);
        }

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [showWinModal, showLossModal]);

    const modalVariants = {
        hidden: {
            opacity: 0,
            scale: 0.8,
            y: -50
        },
        visible: {
            opacity: 1,
            scale: 1,
            y: 0,
            transition: {
                type: "spring",
                damping: 25,
                stiffness: 300
            }
        },
        exit: {
            opacity: 0,
            scale: 0.8,
            y: 50,
            transition: {
                duration: 0.3
            }
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="h-100 d-flex flex-column justify-content-center align-items-center bg-light"
            style={{ minHeight: '100vh' }}
        >
            <div className="text-center mb-4">
                <h1 className="display-4 text-primary mb-3">Trading Platform</h1>
                <p className="lead text-muted">Manage your trades and monitor performance</p>
            </div>

            <div className="d-flex gap-3">
                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="btn btn-success btn-lg px-4 py-2 fw-bold border-0 rounded-3"
                    onClick={() => setShowWinModal(true)}
                    style={{
                        boxShadow: '0 4px 15px rgba(40, 167, 69, 0.3)'
                    }}
                >
                    📈 Winning Trade
                </motion.button>

                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="btn btn-danger btn-lg px-4 py-2 fw-bold border-0 rounded-3"
                    onClick={() => setShowLossModal(true)}
                    style={{
                        boxShadow: '0 4px 15px rgba(220, 53, 69, 0.3)'
                    }}
                >
                    📉 Losing Trade
                </motion.button>
            </div>

            {/* Win Modal */}
            <AnimatePresence>
                {showWinModal && (
                    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <motion.div
                            className="modal-dialog modal-dialog-centered"
                            variants={modalVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                        >
                            <div className="modal-content border-0 shadow-lg rounded-3 overflow-hidden">
                                <div className="modal-header border-0 bg-success text-white position-relative">
                                    <h4 className="modal-title w-100 text-center">
                                        Trade Win
                                    </h4>
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white position-absolute"
                                        style={{ right: '15px', top: '50%', transform: 'translateY(-50%)' }}
                                        onClick={() => setShowWinModal(false)}
                                    />
                                </div>
                                <div className="modal-body text-center py-4">
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{
                                            type: "spring",
                                            stiffness: 200,
                                            delay: 0.2
                                        }}
                                        className="mb-3"
                                    >
                                        <div style={{ fontSize: '4rem' }}>
                                            💰
                                        </div>
                                    </motion.div>
                                    <h5 className="text-success mb-3">Profit Realized!</h5>

                                    {/* Trade Breakdown Table */}
                                    <div className="trade-breakdown mb-4">
                                        <table className="table table-bordered mb-0">
                                            <tbody>
                                                <tr>
                                                    <td className="text-start text-muted px-3">Trade Amount</td>
                                                    <td className="text-end fw-bold px-3">$1,500.00</td>
                                                </tr>
                                                <tr>
                                                    <td className="text-start text-muted px-3">Tax (2%)</td>
                                                    <td className="text-end text-danger px-3">-$30.00</td>
                                                </tr>
                                                <tr>
                                                    <td className="text-start text-muted px-3">Amount After Tax</td>
                                                    <td className="text-end px-3">$1,470.00</td>
                                                </tr>
                                                <tr className="table-success">
                                                    <td className="text-start fw-bold px-3">Win Amount</td>
                                                    <td className="text-end fw-bold text-success px-3">+$2,940.00</td>
                                                </tr>
                                                <tr className="table-primary">
                                                    <td className="text-start fw-bold px-3">Profit</td>
                                                    <td className="text-end fw-bold text-primary px-3">+$1,440.00</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Summary */}
                                    <div className="win-summary mb-3 p-3 bg-primary bg-opacity-10 rounded-3 border border-primary">
                                        <h4 className="text-primary mb-1">Net Profit</h4>
                                        <h2 className="text-primary fw-bold">
                                            +$1,440.00
                                        </h2>
                                        <p className="text-muted small mb-0">
                                            After deducting 2% tax from trade amount
                                        </p>
                                    </div>

                                    <p className="text-muted small">
                                        Your trading strategy paid off! The 2% tax was deducted before the trade,
                                        and you doubled the remaining amount.
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Loss Modal */}
            <AnimatePresence>
                {showLossModal && (
                    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <motion.div
                            className="modal-dialog modal-dialog-centered"
                            variants={modalVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                        >
                            <div className="modal-content border-0 shadow-lg rounded-3 overflow-hidden">
                                <div className="modal-header border-0 bg-danger text-white position-relative">
                                    <h4 className="modal-title w-100 text-center">
                                        Trade Loss
                                    </h4>
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white position-absolute"
                                        style={{ right: '15px', top: '50%', transform: 'translateY(-50%)' }}
                                        onClick={() => setShowLossModal(false)}
                                    />
                                </div>
                                <div className="modal-body text-center py-4">
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{
                                            type: "spring",
                                            stiffness: 200,
                                            delay: 0.2
                                        }}
                                        className="mb-3"
                                    >
                                        <div style={{ fontSize: '4rem' }}>
                                            📉
                                        </div>
                                    </motion.div>
                                    <h5 className="text-danger mb-3">Trade Loss</h5>

                                    {/* Simplified Loss Amount */}
                                    <div className="loss-breakdown mb-4">
                                        <table className="table table-bordered mb-0">
                                            <tbody>
                                                <tr className="table-danger">
                                                    <td className="text-start fw-bold px-3">Total Loss</td>
                                                    <td className="text-end fw-bold text-danger px-3">-$1,500.00</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="loss-summary mb-3 p-3 bg-danger bg-opacity-10 rounded-3 border border-danger border-2">
                                        <h2 className="text-danger fw-bold">
                                            -$1,500.00
                                        </h2>
                                        <p className="text-muted small mb-0">Total Loss Amount</p>
                                    </div>

                                    <p className="text-muted">
                                        This trade didn't go as planned. Remember that risk management is key
                                        to long-term trading success.
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default Test2;